import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import SummaryCards from "../components/SummaryCards";
import TransactionTable from "../components/TransactionTable";
import AddTransactionModal from "../components/AddTransactionModal";
import { getTransactions, addTransaction } from "../services/api";
import { format, parseISO, isSameMonth, isSameDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { SettingsContext } from "../context/SettingsContext";

// Definindo os tipos de pagamento como constante
const PaymentType = {
  All: "Todos",
  Money: "Dinheiro",
  CreditCard: "Cartão de Crédito",
  DebitCard: "Cartão de Débito",
  PIX: "PIX",
  BankTransfer: "Transferência Bancária",
};

// Definindo os tipos de transação que o banco de dados espera
const TransactionTypeDB = {
  Income: "entrada",
  Expense: "saida",
};

// Definindo as opções para o filtro no frontend (display name)
const TransactionFilterOptions = {
  All: "Todos",
  Income: "Entrada",
  Expense: "Saída",
};

const Home = () => {
  const navigate = useNavigate();
  const { preferences, isLoadingPreferences, errorLoadingPreferences } =
    useContext(SettingsContext);

  const [allTransactions, setAllTransactions] = useState([]);
  const [displayedTransactions, setDisplayedTransactions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    format(new Date(), "yyyy-MM")
  );
  const [selectedDay, setSelectedDay] = useState(
    format(new Date(), "yyyy-MM-dd")
  );

  const paymentTypes = useMemo(() => Object.values(PaymentType), []);
  const [selectedPaymentType, setSelectedPaymentType] = useState(
    PaymentType.All
  );
  const [selectedTransactionType, setSelectedTransactionType] = useState(
    TransactionFilterOptions.All
  );

  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [totals, setTotals] = useState({
    dailyIncome: 0,
    monthlyIncome: 0,
    yearlyIncome: 0,
    dailyExpense: 0,
    monthlyExpense: 0,
    yearlyExpense: 0,
    dailyBalance: 0,
    monthlyBalance: 0,
    yearlyBalance: 0,
    paymentMethodsIncome: {},
    paymentMethodsExpense: {},
    paymentMethodsDailyIncome: {},
    paymentMethodsDailyExpense: {},
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Inicialização robusta de showValues.
  // Usamos uma função para inicializar o estado, que é chamada APENAS UMA VEZ.
  // Ela usa 'preferences.defaultShowValues' se disponível, caso contrário, 'true' como padrão.
  const [showValues, setShowValues] = useState(() => {
    // Isso será executado apenas na primeira renderização do componente.
    // Se preferences for undefined (o que é coberto pelo isLoadingPreferences abaixo),
    // ele usará o 'true' como fallback.
    return preferences?.defaultShowValues ?? true;
  });

  // REMOVIDO: O useEffect que estava sobrescrevendo o estado showValues a cada renderização.
  // Agora, showValues é inicializado uma vez, e depois controlado apenas pelo toggle.
  // useEffect(() => {
  //     if (preferences) {
  //         setShowValues(preferences.defaultShowValues);
  //     }
  // }, [preferences]);

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
      return;
    }
    // Só carrega as transações se as preferências já tiverem sido carregadas
    if (!isLoadingPreferences) {
      fetchAllTransactions();
    }
  }, [navigate, isLoadingPreferences]);

  const fetchAllTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getTransactions();
      setAllTransactions(response.data || []);
      setCurrentPage(1);
    } catch (err) {
      setError("Erro ao carregar transações.");
      console.error(err);
      if (err.response?.status === 401) navigate("/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const paymentTypeMapping = useMemo(() => {
    if (!allTransactions || allTransactions.length === 0) {
      return {};
    }
    const typesInData = [
      ...new Set(
        allTransactions
          .map((t) => t.tipoPagamento || t.forma_pagamento)
          .filter(Boolean)
      ),
    ];
    const mapping = {};
    typesInData.forEach((type) => {
      const matchedType =
        paymentTypes.find(
          (pt) =>
            pt.toLowerCase().includes(type.toLowerCase()) ||
            type.toLowerCase().includes(pt.toLowerCase())
        ) || type;
      mapping[type] = matchedType;
    });
    return mapping;
  }, [allTransactions, paymentTypes]);

  const totalPages = useMemo(() => {
    if (!allTransactions || allTransactions.length === 0) return 1;

    const monthDate = parseISO(`${selectedMonth}-01`);
    let filtered = allTransactions.filter(
      (t) => t?.data && isSameMonth(parseISO(t.data), monthDate)
    );

    if (selectedDay) {
      filtered = filtered.filter(
        (t) => t?.data && isSameDay(parseISO(t.data), parseISO(selectedDay))
      );
    }

    if (selectedPaymentType !== PaymentType.All) {
      filtered = filtered.filter((t) => {
        const originalType = t.forma_pagamento || t.tipoPagamento || "";
        const mappedType = paymentTypeMapping[originalType] || originalType;
        return (
          mappedType.trim().toLowerCase() ===
          selectedPaymentType.trim().toLowerCase()
        );
      });
    }

    if (selectedTransactionType !== TransactionFilterOptions.All) {
      const dbType =
        selectedTransactionType === TransactionFilterOptions.Income
          ? TransactionTypeDB.Income
          : TransactionTypeDB.Expense;
      filtered = filtered.filter((t) => t.tipo === dbType);
    }

    const totalItems = filtered.length;
    const itemsPerPageValue =
      itemsPerPage === "all" ? totalItems : Number(itemsPerPage);
    return Math.ceil(totalItems / (itemsPerPageValue || 1));
  }, [
    allTransactions,
    selectedMonth,
    selectedDay,
    selectedPaymentType,
    itemsPerPage,
    paymentTypeMapping,
    selectedTransactionType,
  ]);

  useEffect(() => {
    let currentFilteredTransactions = [];
    if (allTransactions && allTransactions.length > 0) {
      const monthDate = parseISO(`${selectedMonth}-01`);

      currentFilteredTransactions = allTransactions
        .filter((t) => t?.data && isSameMonth(parseISO(t.data), monthDate))
        .sort(
          (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
        );

      if (selectedDay) {
        currentFilteredTransactions = currentFilteredTransactions.filter(
          (t) => t?.data && isSameDay(parseISO(t.data), parseISO(selectedDay))
        );
      }

      if (selectedPaymentType !== PaymentType.All) {
        currentFilteredTransactions = currentFilteredTransactions.filter(
          (t) => {
            const originalType = t.forma_pagamento || t.tipoPagamento || "";
            const mappedType = paymentTypeMapping[originalType] || originalType;
            return (
              mappedType.trim().toLowerCase() ===
              selectedPaymentType.trim().toLowerCase()
            );
          }
        );
      }

      if (selectedTransactionType !== TransactionFilterOptions.All) {
        const dbType =
          selectedTransactionType === TransactionFilterOptions.Income
            ? TransactionTypeDB.Income
            : TransactionTypeDB.Expense;
        currentFilteredTransactions = currentFilteredTransactions.filter(
          (t) => t.tipo === dbType
        );
      }
    }

    const totalItems = currentFilteredTransactions.length;
    const itemsPerPageValue =
      itemsPerPage === "all" ? totalItems : Number(itemsPerPage);
    const newTotalPages = Math.ceil(totalItems / (itemsPerPageValue || 1));

    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    } else if (newTotalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    } else if (currentPage <= 0) {
      setCurrentPage(1);
    }

    const selectedYear = parseISO(`${selectedMonth}-01`).getFullYear();
    const monthDate = parseISO(`${selectedMonth}-01`);

    const newPaymentMethodsIncomeTotals = {};
    const newPaymentMethodsExpenseTotals = {};
    const newPaymentMethodsDailyIncomeTotals = {};
    const newPaymentMethodsDailyExpenseTotals = {};

    paymentTypes
      .filter((type) => type !== PaymentType.All)
      .forEach((type) => {
        newPaymentMethodsIncomeTotals[type] = 0;
        newPaymentMethodsExpenseTotals[type] = 0;
        newPaymentMethodsDailyIncomeTotals[type] = 0;
        newPaymentMethodsDailyExpenseTotals[type] = 0;
      });

    const aggregatePaymentsByType = (
      transactionsToAggregate,
      totalsMapIncome,
      totalsMapExpense
    ) => {
      transactionsToAggregate.forEach((t) => {
        const amount = Number(t.valor) || 0;
        const originalType = t.forma_pagamento || t.tipoPagamento || "";
        const type = paymentTypeMapping[originalType] || originalType;
        if (type && type !== PaymentType.All) {
          if (t.tipo === TransactionTypeDB.Income) {
            totalsMapIncome[type] = (totalsMapIncome[type] || 0) + amount;
          } else if (t.tipo === TransactionTypeDB.Expense) {
            totalsMapExpense[type] = (totalsMapExpense[type] || 0) + amount;
          }
        }
      });
    };

    const transactionsForMonth = allTransactions.filter(
      (t) => t?.data && isSameMonth(parseISO(t.data), monthDate)
    );
    aggregatePaymentsByType(
      transactionsForMonth,
      newPaymentMethodsIncomeTotals,
      newPaymentMethodsExpenseTotals
    );
    const monthlyIncomeTotal = transactionsForMonth
      .filter((t) => t.tipo === TransactionTypeDB.Income)
      .reduce((sum, t) => sum + (Number(t.valor) || 0), 0);
    const monthlyExpenseTotal = transactionsForMonth
      .filter((t) => t.tipo === TransactionTypeDB.Expense)
      .reduce((sum, t) => sum + (Number(t.valor) || 0), 0);
    const monthlyBalance = monthlyIncomeTotal - monthlyExpenseTotal;

    let dailyIncomeTotal = 0;
    let dailyExpenseTotal = 0;
    if (selectedDay) {
      const transactionsForDay = allTransactions.filter(
        (t) => t?.data && isSameDay(parseISO(t.data), parseISO(selectedDay))
      );
      aggregatePaymentsByType(
        transactionsForDay,
        newPaymentMethodsDailyIncomeTotals,
        newPaymentMethodsDailyExpenseTotals
      );
      dailyIncomeTotal = transactionsForDay
        .filter((t) => t.tipo === TransactionTypeDB.Income)
        .reduce((sum, t) => sum + (Number(t.valor) || 0), 0);
      dailyExpenseTotal = transactionsForDay
        .filter((t) => t.tipo === TransactionTypeDB.Expense)
        .reduce((sum, t) => sum + (Number(t.valor) || 0), 0);
    }
    const dailyBalance = dailyIncomeTotal - dailyExpenseTotal;

    const yearlyIncomeTotal = allTransactions
      .filter(
        (t) =>
          t?.data &&
          parseISO(t.data).getFullYear() === selectedYear &&
          t.tipo === TransactionTypeDB.Income
      )
      .reduce((sum, t) => sum + (Number(t.valor) || 0), 0);
    const yearlyExpenseTotal = allTransactions
      .filter(
        (t) =>
          t?.data &&
          parseISO(t.data).getFullYear() === selectedYear &&
          t.tipo === TransactionTypeDB.Expense
      )
      .reduce((sum, t) => sum + (Number(t.valor) || 0), 0);
    const yearlyBalance = yearlyIncomeTotal - yearlyExpenseTotal;

    setTotals({
      dailyIncome: dailyIncomeTotal,
      monthlyIncome: monthlyIncomeTotal,
      yearlyIncome: yearlyIncomeTotal,
      dailyExpense: dailyExpenseTotal,
      monthlyExpense: monthlyExpenseTotal,
      yearlyExpense: yearlyExpenseTotal,
      dailyBalance: dailyBalance,
      monthlyBalance: monthlyBalance,
      yearlyBalance: yearlyBalance,
      paymentMethodsIncome: newPaymentMethodsIncomeTotals,
      paymentMethodsExpense: newPaymentMethodsExpenseTotals,
      paymentMethodsDailyIncome: newPaymentMethodsDailyIncomeTotals,
      paymentMethodsDailyExpense: newPaymentMethodsDailyExpenseTotals,
    });

    const startIndex = (currentPage - 1) * itemsPerPageValue;
    const endIndex = startIndex + itemsPerPageValue;

    setDisplayedTransactions(
      itemsPerPage === "all"
        ? currentFilteredTransactions
        : currentFilteredTransactions.slice(startIndex, endIndex)
    );
  }, [
    allTransactions,
    selectedMonth,
    selectedDay,
    selectedPaymentType,
    paymentTypeMapping,
    currentPage,
    itemsPerPage,
    paymentTypes,
    selectedTransactionType,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "=") {
        event.preventDefault();
        event.stopPropagation();
        setShowAddModal(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleMonthChange = useCallback((e) => {
    setSelectedMonth(e.target.value);
    setSelectedDay("");
    setCurrentPage(1);
  }, []);

  const handleDayChange = useCallback((e) => {
    const date = e.target.value;
    if (date) {
      const [year, month] = date.split("-");
      setSelectedMonth(`${year}-${month}`);
      setSelectedDay(date);
    } else {
      setSelectedDay("");
    }
    setCurrentPage(1);
  }, []);

  const handlePaymentTypeChange = useCallback((e) => {
    setSelectedPaymentType(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleTransactionTypeChange = useCallback((e) => {
    setSelectedTransactionType(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleItemsPerPageChange = useCallback((e) => {
    const value = e.target.value;
    setItemsPerPage(value === "all" ? "all" : Number(value));
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback(
    (newPage) => {
      setCurrentPage((prev) => {
        if (newPage >= 1 && newPage <= totalPages) {
          return newPage;
        }
        return prev;
      });
    },
    [totalPages]
  );

  const handleSaveTransaction = useCallback(
    async (transactionData) => {
      try {
        await addTransaction(transactionData);
        fetchAllTransactions();
      } catch (error) {
        console.error("Erro ao salvar transação:", error);
        throw error;
      }
    },
    [fetchAllTransactions]
  );

  const toggleShowValues = useCallback(() => {
    setShowValues((prev) => !prev);
  }, []);

  // Se as preferências ainda estão carregando, mostre uma mensagem de carregamento.
  if (isLoadingPreferences) {
    return (
      <div>
        <Header />
        <div
          className="container"
          style={{
            maxWidth: "1200px",
            margin: "50px auto",
            padding: "0 15px",
            textAlign: "center",
          }}
        >
          <p>Carregando preferências do sistema...</p>
          {errorLoadingPreferences && (
            <div
              style={{
                backgroundColor: "#f8d7da",
                color: "#721c24",
                padding: "10px",
                borderRadius: "4px",
                marginBottom: "20px",
                border: "1px solid #f5c6cb",
              }}
            >
              {errorLoadingPreferences}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div
        className="container"
        style={{ maxWidth: "1200px", margin: "50px auto", padding: "0 15px" }}
      >
        {error && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              color: "#721c24",
              padding: "10px",
              borderRadius: "4px",
              marginBottom: "20px",
              border: "1px solid #f5c6cb",
            }}
          >
            {error}
          </div>
        )}

        {/* Cards de Resumo */}
        <SummaryCards
          totals={totals}
          month={selectedMonth}
          day={selectedDay}
          paymentTypes={paymentTypes.filter((t) => t !== PaymentType.All)}
          showValues={showValues}
        />

        {/* BOTÃO DE MOSTRAR/OCULTAR VALORES */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            marginTop: "-35px",
            marginBottom: "15px",
            cursor: "pointer",
          }}
          onClick={toggleShowValues}
        >
          <span style={{ marginRight: "8px", fontSize: "18px" }}>
            {showValues ? "👁️" : "🙈"}
          </span>
          <span style={{ fontWeight: "600", color: "#555", fontSize: "14px" }}>
            {showValues ? "Ocultar Valores" : "Mostrar Valores"}
          </span>
        </div>

        {/* Seção de Filtros */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: "10px",
            margin: "15px 0",
            flexWrap: "wrap",
          }}
        >
          {/* Filtro de Mês */}
          <div style={{ flex: "1", minWidth: "180px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "3px",
                fontWeight: "600",
                color: "#555",
                fontSize: "12px",
              }}
            >
              Mês
            </label>
            <input
              type="month"
              value={selectedMonth}
              onChange={handleMonthChange}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>

          {/* Filtro de Dia */}
          <div style={{ flex: "1", minWidth: "180px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "3px",
                fontWeight: "600",
                color: "#555",
                fontSize: "12px",
              }}
            >
              Dia (opcional)
            </label>
            <input
              type="date"
              value={selectedDay || ""}
              onChange={handleDayChange}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>

          {/* Filtro de Tipo de Pagamento */}
          <div style={{ flex: "1", minWidth: "180px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "3px",
                fontWeight: "600",
                color: "#555",
                fontSize: "12px",
              }}
            >
              Tipo de Pagamento
            </label>
            <select
              value={selectedPaymentType}
              onChange={handlePaymentTypeChange}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              {paymentTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Novo filtro para Tipo de Transação */}
          <div style={{ flex: "1", minWidth: "180px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "3px",
                fontWeight: "600",
                color: "#555",
                fontSize: "12px",
              }}
            >
              Tipo de Transação
            </label>
            <select
              value={selectedTransactionType}
              onChange={handleTransactionTypeChange}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              {Object.values(TransactionFilterOptions).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Botão de Adicionar */}
          <button
            style={{
              padding: "8px 15px",
              backgroundColor: "#2ecc71",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "14px",
              height: "36px",
              minWidth: "150px",
            }}
            onClick={() => setShowAddModal(true)}
          >
            Adicionar Transação
          </button>
        </div>

        {/* Controles de Paginação */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            margin: "15px 0",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{ fontWeight: "600", color: "#555", fontSize: "12px" }}
            >
              Mostrar:
            </span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              style={{
                padding: "6px 10px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "12px",
              }}
            >
              <option value="25">25 registros</option>
              <option value="50">50 registros</option>
              <option value="100">100 registros</option>
              <option value="all">Todos</option>
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{ fontWeight: "600", color: "#555", fontSize: "12px" }}
            >
              Exibindo {displayedTransactions.length} de{" "}
              {allTransactions.length} registros
            </span>
          </div>

          {itemsPerPage !== "all" && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 10px",
                  backgroundColor: currentPage === 1 ? "#ddd" : "#3498db",
                  color: currentPage === 1 ? "#666" : "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontSize: "12px",
                }}
              >
                Anterior
              </button>
              <span
                style={{ fontWeight: "600", color: "#555", fontSize: "12px" }}
              >
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 10px",
                  backgroundColor:
                    currentPage === totalPages ? "#ddd" : "#3498db",
                  color: currentPage === totalPages ? "#666" : "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor:
                    currentPage === totalPages ? "not-allowed" : "pointer",
                  fontSize: "12px",
                }}
              >
                Próxima
              </button>
            </div>
          )}
        </div>

        {/* Tabela de Transações */}
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "150px",
            }}
          >
            <p style={{ fontSize: "13px" }}>Carregando transações...</p>
          </div>
        ) : (
          <TransactionTable
            transactions={displayedTransactions}
            onRefresh={fetchAllTransactions}
          />
        )}

        {/* Modal de Adição */}
        {showAddModal && (
          <AddTransactionModal
            onClose={() => setShowAddModal(false)}
            onSave={handleSaveTransaction}
            currentDate={selectedDay || format(new Date(), "yyyy-MM-dd")}
          />
        )}
      </div>
    </div>
  );
};

export default Home;
